import {
  arrow,
  autoUpdate,
  computePosition,
  flip,
  limitShift,
  offset,
  Placement,
  shift,
  size,
} from "@floating-ui/react";
import {
  ComponentPropsWithoutRef,
  useCallback,
  useContext,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { DisableCloseOnEscapeOrClickProps } from "../../types";
import { DialogContext } from "../Dialog";
import { PopoverButton } from "./PopoverButton";
import { PopoverClose } from "./PopoverClose";
import { PopoverContent } from "./PopoverContent";
import { PopoverTrigger } from "./PopoverTrigger";
import { useKeyboardFocusables, useOnClickOutside } from "../../hooks";

import { supportsPopover } from "../../internal/functions/supportsPopover";
import { PopoverLegacyElement } from "../PopoverLegacy/PopoverLegacy";
import { PopoverLegacyProps } from "../PopoverLegacy";
import { PopoverContext, PopoverContextProps } from "./PopoverContext";
import {
  safeHidePopover,
  safeShowPopover,
} from "../../internal/functions/safePopover";
import { OpenCloseTransitionStateEffects } from "../../hooks/useOpenCloseTransitionStates";
import { usePopoverTransitionStates } from "../../hooks/usePopoverTransitionStates";

/**
 * Props for the Popover component
 * @extends ComponentPropsWithoutRef<"div">
 * @extends DisableCloseOnEscapeOrClickProps
 */
export type PopoverProps = {
  /**
   * The content to be rendered inside the popover
   */
  children?: ComponentPropsWithoutRef<"div">["children"];
  /**
   * Unique identifier for the popover
   */
  id?: ComponentPropsWithoutRef<"div">["id"];
  /**
   * Whether the popover should be modal (traps focus)
   * @default false
   */
  modal?: boolean;
  /**
   * Disables the shift of floating element
   * @internal
   */
  disableShift?: boolean;
  /**
   * Disables the flip of floating element
   * @internal
   */
  disableFlip?: boolean;
  /**
   * Disables the flip cross-axis fallback of floating element
   * @internal
   */
  disableFlipFallback?: boolean;
  /**
   * Changes interaction to use hover instead of click
   * @default false
   */
  openOnHover?: boolean;
  /**
   * Changes height and positioning for select/combobox
   * @default false
   */
  fillAvailableHeight?: boolean;
  /**
   * Maximum height of the popover content
   */
  maxHeight?: number | string;
  /**
   * Whether the popover should fit within the screen bounds
   * @default false
   */
  fitScreen?: boolean;
  /**
   * Minimum height of the popover content
   * @default "2.875rem"
   */
  minHeight?: number | string;
  /**
   * Whether the popover width should match the trigger element
   * @default false
   */
  matchReferenceWidth?: boolean;
  /**
   * Whether the popover should have a fixed width
   * @default false
   */
  fixedWidth?: boolean;
  /**
   * Placement of the popover relative to the trigger
   * @default "bottom"
   */
  placement?: Placement;
  /**
   * Controlled open state
   */
  open?: boolean;
  /**
   * Default open state for uncontrolled mode
   * @default false
   */
  defaultOpen?: boolean;
  /**
   * @deprecated to be removed in next major
   * use onOpenAnimationStart, onOpenAnimationComplete, onCloseAnimationStart, onCloseAnimationComplete instead
   * Callback when open state changes
   */
  onOpenChange?: (open: boolean) => void;
  /**
   * @deprecated to be removed in next major
   * use onClickOutside instead
   * Callback when clicking outside the popover
   */
  onOutsidePress?: (e: globalThis.MouseEvent) => void;
  /**
   * Callback when clicking outside the popover
   */
  onClickOutside?: (e: globalThis.MouseEvent) => void;
  /**
   * Fires when the floating element is requested to be closed
   * Note: this does not fire when openOnHover is true and mouse leaves to close
   * eg. on click outside, [esc] to close, or by <Popover.Close>
   */
  onClose?: () => void;
  /**
   * Whether to disable the caret/arrow indicator
   * @default false
   */
  disableCaret?: boolean;
  /**
   * Whether to remove default padding from content
   * @default false
   */
  noPadding?: boolean;
  /**
   * Whether to disable automatic position updates
   * @default false
   */
  disableAutoUpdate?: boolean;
  /**
   * @deprecated to be removed on next major
   * use modal instead
   */
  disableFocusLock?: boolean;
  /**
   * Whether to disable focus on the trigger element
   * @default false
   */
  disableTriggerFocus?: boolean;
  /**
   * Delay before opening the popover (for hover mode)
   * @default 100
   */
  delay?: number;
  /**
   * @deprecated to be removed on next major
   * This is not needed anymore as the Popover API is used.
   * Popover API uses top-layer for its popover content.
   */
  root?: HTMLElement | React.MutableRefObject<HTMLElement | null> | null;
} & DisableCloseOnEscapeOrClickProps &
  OpenCloseTransitionStateEffects;

export const PopoverElement = (props: PopoverProps) => {
  const {
    children,
    openOnHover = false,
    delay = 100,
    placement = "bottom",
    minHeight = "2.875rem",
    open,
    defaultOpen = false,
    disableShift,
    disableFlip,
    disableFlipFallback,
    matchReferenceWidth,
    id,
    disableCaret,
    fixedWidth,
    fillAvailableHeight,
    maxHeight,
    disableCloseOnClickOutside,
    disableCloseOnEscape,
    fitScreen,
    disableFocusLock,
    disableTriggerFocus,
    modal,
    noPadding,
    onOpenChange,
    onOutsidePress,
    onClickOutside,
    onClose,
    onOpenAnimationStart,
    onOpenAnimationComplete,
    onCloseAnimationStart,
    onCloseAnimationComplete,
  } = props;
  const uid = useId();
  const [invoker, setInvoker] =
    useState<PopoverContextProps["invoker"]>(undefined);
  const [popover, setPopover] =
    useState<PopoverContextProps["popover"]>(undefined);
  const [rootNode, setRootNode] = useState<PopoverContextProps["rootNode"]>();
  const [arrowElement, setArrowElement] =
    useState<PopoverContextProps["arrowElement"]>(undefined);
  const [openState, setOpenState] = useState<PopoverContextProps["openState"]>(
    open ?? defaultOpen,
  );
  const arrowRef = useRef<HTMLSpanElement>(null);
  const dialogContext = useContext(DialogContext);

  // TODO: Find a way to test these properly!
  // Stryker disable all : Currently relying on Chromatic test for all visuals
  const processMinHeight =
    typeof minHeight === "string" ? minHeight : `${minHeight}px`;

  usePopoverTransitionStates(popover, openState, {
    onOpenAnimationStart: useCallback(() => {
      onOpenAnimationStart?.();
    }, [onOpenAnimationStart]),
    onOpenAnimationComplete: useCallback(() => {
      onOpenAnimationComplete?.();
    }, [onOpenAnimationComplete]),
    onCloseAnimationStart: useCallback(() => {
      onCloseAnimationStart?.();
    }, [onCloseAnimationStart]),
    onCloseAnimationComplete: useCallback(() => {
      onCloseAnimationComplete?.();
    }, [onCloseAnimationComplete]),
  });

  /* istanbul ignore next */
  const updatePosition = useCallback(() => {
    if (!invoker || !popover) return;
    // disable position update when closing
    if (!openState) return;
    computePosition(invoker, popover, {
      placement: placement,
      middleware: [
        offset(8),
        size({
          padding: 5,
          apply({ rects, availableHeight, elements }) {
            Object.assign(elements.floating.style, {
              ...(matchReferenceWidth
                ? { width: `${rects.reference.width}px` }
                : fixedWidth
                  ? { width: "40dvw" }
                  : {}),
              ...(fillAvailableHeight
                ? {
                    height: `${availableHeight - 20}px`,
                    minHeight: processMinHeight,
                  }
                : {}),
              ...(maxHeight
                ? {
                    minHeight: processMinHeight,
                    height: fitScreen
                      ? typeof maxHeight === "string"
                        ? maxHeight
                        : `${maxHeight}px`
                      : undefined,
                    maxHeight: fitScreen
                      ? `${availableHeight - 20}px`
                      : typeof maxHeight === "string"
                        ? maxHeight
                        : `${maxHeight}px`,
                  }
                : {}),
              ...(fitScreen && !maxHeight
                ? {
                    minHeight: processMinHeight,
                    maxHeight: `${availableHeight - 20}px`,
                  }
                : {}),
              zIndex: 1,
            });
          },
        }),
        flip({
          mainAxis: !disableFlip,
          crossAxis: false,
          fallbackAxisSideDirection: disableFlipFallback ? "none" : "end",
          padding: 5,
        }),
        shift({
          padding: 5,
          mainAxis: !disableShift,
          crossAxis: false,
          limiter: limitShift({
            mainAxis: !disableShift,
          }),
        }),
        arrow({
          element: !disableCaret && arrowElement ? arrowElement : null,
          padding: 5,
        }),
      ],
    }).then(({ x, y, placement, middlewareData }) => {
      const side = placement.split("-")[0];

      const staticSide = {
        top: "bottom",
        right: "left",
        bottom: "top",
        left: "right",
      }[side];

      const arrowDeg = () => {
        if (staticSide === "top") return 180;
        if (staticSide === "bottom") return 0;
        if (staticSide === "left") return 90;
        return 270;
      };

      Object.assign(popover.style, {
        left: `${x}px`,
        top: `${y}px`,
      });
      if (middlewareData.arrow && arrowElement) {
        const { x, y } = middlewareData.arrow;
        Object.assign(arrowElement.style, {
          left: x != null ? `${x}px` : "",
          top: y != null ? `${y}px` : "",
          right: "",
          bottom: "",
          [staticSide as string]: `${-arrowElement.offsetWidth + 2}px`,
          transform: `rotate(${arrowDeg()}deg)`,
        });
      }
    });
  }, [
    arrowElement,
    disableCaret,
    disableFlip,
    disableFlipFallback,
    disableShift,
    fillAvailableHeight,
    fitScreen,
    fixedWidth,
    invoker,
    matchReferenceWidth,
    maxHeight,
    openState,
    placement,
    popover,
    processMinHeight,
  ]);

  const resetKey = useRef(0);
  // Effect to add autoUpdate to eventListener
  // while the popover is opened
  useLayoutEffect(() => {
    if (!popover || !invoker) return;
    if (!openState) {
      resetKey.current += 1;
      return;
    }

    let requestID: number | null = null;
    const cleanup = autoUpdate(invoker, popover, () => {
      requestID = requestAnimationFrame(updatePosition);
    });

    return () => {
      if (requestID) cancelAnimationFrame(requestID);
      cleanup();
    };
  }, [invoker, popover, updatePosition, openState]);
  // Stryker restore all

  popover?.getRootNode();

  const { focusables } = useKeyboardFocusables(popover, {
    observeChange: true,
  });

  const popoverId = id ?? `popover-${uid.replace(/:/g, "")}`;
  const isControlled = typeof open !== "undefined";
  const openTimeoutRef = useRef<number | NodeJS.Timeout | null>(null);
  const closeTimeoutRef = useRef<number | NodeJS.Timeout | null>(null);

  // Main function to open popover
  const openPopover = useCallback(() => {
    clearTimeout(closeTimeoutRef.current as number);
    clearTimeout(openTimeoutRef.current as number);
    if (
      !popover ||
      !(rootNode as ShadowRoot | Document)?.querySelector(`#${popoverId}`)
    )
      return;
    openTimeoutRef.current = setTimeout(() => {
      safeShowPopover(popover);
      setOpenState(true);
      onOpenChange?.(true);
      dialogContext?.setHasOpenPopover?.(true);
    }, delay);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- onOpenChange is intentionally omitted to prevent the openPopover function from being recreated on every render, which could cause unnecessary re-runs of the controlled useEffect and potential infinite loops
  }, [popover, rootNode, popoverId, delay, dialogContext]);

  // Main function to close popover
  const closePopover = useCallback(
    (fn?: () => void) => {
      clearTimeout(openTimeoutRef.current as number);
      clearTimeout(closeTimeoutRef.current as number);
      if (!popover || !openState) return;
      closeTimeoutRef.current = setTimeout(() => {
        if (!openOnHover) onClose?.();
        setOpenState(false);
        onOpenChange?.(false);
        fn?.();
        safeHidePopover(popover);
        dialogContext?.setHasOpenPopover?.(false);
      }, 100);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- onClose and onOpenChange are intentionally omitted to prevent the closePopover function from being recreated on every render, which could cause unnecessary re-runs of the controlled useEffect and potential infinite loops
    [popover, openState, openOnHover, dialogContext],
  );

  // openness for initial render
  const initialRender = useRef(true);
  useEffect(() => {
    if (initialRender.current === true) {
      if (openState) openPopover();
      initialRender.current = false;
      return;
    }
  }, [openState, openPopover]);

  // Updating openness for controlled
  useEffect(() => {
    if (typeof open === "undefined") return;
    if (open) {
      openPopover();
    } else {
      closePopover();
    }
  }, [open, openPopover, closePopover]);

  useOnClickOutside({
    onOutsidePress: (e) => {
      if (!open) return;
      onClickOutside?.(e);
      onOutsidePress?.(e);
    },
    targets: [invoker, popover],
    onClose: () => {
      if (
        disableCloseOnClickOutside ||
        openOnHover ||
        isControlled ||
        !openState
      )
        return;
      closePopover();
    },
  });

  const value = useMemo(() => {
    return {
      popoverId,
      invoker,
      setInvoker,
      popover,
      setPopover,
      arrowElement,
      setArrowElement,
      openPopover,
      closePopover,
      placement,
      onClose,
      openOnHover,
      openState,
      setOpenState,
      disableCloseOnClickOutside,
      fillAvailableHeight,
      maxHeight,
      matchReferenceWidth,
      fixedWidth,
      disableCaret,
      disableFlipFallback,
      disableFocusLock,
      disableTriggerFocus,
      disableCloseOnEscape,
      noPadding,
      controlled: isControlled,
      modal: modal ?? disableFocusLock,
      arrowRef,
      resetKey: resetKey.current,
      rootNode,
      setRootNode,
      focusables,
    };
  }, [
    popoverId,
    invoker,
    popover,
    arrowElement,
    openPopover,
    closePopover,
    placement,
    onClose,
    openOnHover,
    openState,
    setOpenState,
    disableCloseOnClickOutside,
    fillAvailableHeight,
    maxHeight,
    matchReferenceWidth,
    fixedWidth,
    disableCaret,
    disableFlipFallback,
    disableFocusLock,
    disableTriggerFocus,
    disableCloseOnEscape,
    noPadding,
    isControlled,
    modal,
    rootNode,
    focusables,
  ]);

  if (!supportsPopover())
    return <PopoverLegacyElement {...(props as PopoverLegacyProps)} />;

  return (
    <PopoverContext.Provider value={value}>{children}</PopoverContext.Provider>
  );
};

/**
 * Popover component for displaying floating content relative to a trigger element.
 *
 * Features:
 * - Supports both controlled and uncontrolled modes
 * - Automatic positioning with fallback strategies
 * - Hover and click interaction modes
 * - Modal and non-modal variants
 * - Custom trigger elements
 * - Automatic height and width management
 * - Screen boundary detection and fitting
 * - Keyboard navigation and accessibility
 * - Popover API support with legacy fallback
 * - Outside click detection and handling
 * - Focus management and trapping
 * - Arrow/caret indicators
 * - Delay support for hover interactions
 *
 * @example
 * <Popover>
 *   <Popover.Button>Click to open</Popover.Button>
 *   <Popover.Content>
 *     <p>This is the popover content</p>
 *     <Popover.Close>Close</Popover.Close>
 *   </Popover.Content>
 * </Popover>
 *
 * @example
 * <Popover openOnHover delay={500}>
 *   <Popover.Button>Hover to open</Popover.Button>
 *   <Popover.Content>Tooltip content</Popover.Content>
 * </Popover>
 *
 * @example
 * <Popover modal placement="top">
 *   <Popover.Trigger>
 *     {(props) => <button {...props}>Custom trigger</button>}
 *   </Popover.Trigger>
 *   <Popover.Content>
 *     <TextField label="Form field" />
 *     <Popover.Close>Done</Popover.Close>
 *   </Popover.Content>
 * </Popover>
 */
export const Popover = Object.assign(PopoverElement, {
  /**
   * PopoverTrigger component for creating custom trigger elements.
   *
   * Features:
   * - Supports custom trigger elements via render prop pattern
   * - Automatic focus management and keyboard interaction
   * - Hover and click event handling
   * - Accessibility attributes (aria-haspopup, aria-expanded, aria-details)
   * - Legacy popover support with fallback
   * - Context integration with parent popover
   *
   * @example
   * <Popover.Trigger>
   *   {(props) => (
   *     <button {...props}>
   *       Click to open popover
   *     </button>
   *   )}
   * </Popover.Trigger>
   */
  Trigger: PopoverTrigger,
  /**
   * PopoverClose component for closing the popover.
   *
   * Features:
   * - Automatic popover closing on click
   * - Supports all Button component props and styling
   * - Controlled and uncontrolled mode support
   * - Legacy popover support with fallback
   * - Context integration with parent popover
   * - Accessibility support with proper button semantics
   * - Delayed closing for controlled mode
   *
   * @example
   * <Popover.Close>Close</Popover.Close>
   */
  Close: PopoverClose,
  /**
   * PopoverContent component for the main popover content area.
   *
   * Features:
   * - Automatic positioning and sizing based on trigger
   * - Screen boundary detection and fitting
   * - Scroll handling with custom scroller reference
   * - Focus management and keyboard navigation
   * - Hover interaction support for tooltip mode
   * - Escape key handling for closing
   * - Tab key trapping for modal popovers
   * - Arrow/caret indicator with automatic positioning
   * - Legacy popover support with fallback
   * - Context integration with parent popover
   * - Accessibility support with proper ARIA roles
   *
   * @example
   * <Popover.Content>
   *   <p>This is the popover content</p>
   *   <Popover.Close>Close</Popover.Close>
   * </Popover.Content>
   */
  Content: PopoverContent,
  /**
   * PopoverButton component for creating button-style triggers.
   *
   * Features:
   * - Button styling and behavior with all Button props
   * - Automatic popover opening on click
   * - Hover interaction support for tooltip mode
   * - Focus management and keyboard navigation
   * - Accessibility attributes (aria-haspopup, aria-expanded, aria-details)
   * - Legacy popover support with fallback
   * - Context integration with parent popover
   * - Event handling for mouse, keyboard, and focus events
   *
   * @example
   * <Popover.Button>Click to open</Popover.Button>
   */
  Button: PopoverButton,
  Context: PopoverContext,
});
